import { FC } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PlaneIcon, ShipIcon, TrainIcon, TruckIcon, WaterIcon } from "../../../../../assets/icons";
import { TransportMode } from "../../../../../datasources/carbonsnap/models";

const TRANSPORT_MODE_OPTIONS = [
	{ mode: TransportMode.Road, icon: TruckIcon, labelKey: "arc/controls/mode/road" },
	{ mode: TransportMode.Train, icon: TrainIcon, labelKey: "arc/controls/mode/train" },
	{ mode: TransportMode.Sea, icon: ShipIcon, labelKey: "arc/controls/mode/sea" },
	{ mode: TransportMode.Air, icon: PlaneIcon, labelKey: "arc/controls/mode/air" },
	{ mode: TransportMode.Water, icon: WaterIcon, labelKey: "arc/controls/mode/inland waterway" },
] as const;

interface Props {
	value?: TransportMode;
	onChange: (mode: TransportMode) => void;
}

/**
 * Shared icon-pill selector for the transport mode of an arc.
 * Used both inline in ArcControls and inside ArcDetailsDialog to avoid duplicating the same markup.
 */
const TransportModePicker: FC<Props> = ({ value, onChange }) => {
	const { t } = useTranslation("feature.carbonsnap");

	return (
		<Box
			sx={{
				display: "inline-flex",
				gap: 1,
				padding: 0.5,
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
			}}
		>
			{TRANSPORT_MODE_OPTIONS.map(({ mode, icon: Icon, labelKey }) => {
				const label = t(labelKey);
				return (
					<Tooltip key={mode} title={label} placement="top" arrow>
						<IconButton
							size="small"
							onClick={() => onChange(mode)}
							aria-label={label}
							sx={{
								borderRadius: 1,
								backgroundColor: value === mode ? "grey.300" : "common.white",
								"&:hover": {
									backgroundColor: value === mode ? "grey.300" : "secondary.50",
								},
							}}
						>
							<Icon fontSize="small" sx={{ color: "secondary.main" }} />
						</IconButton>
					</Tooltip>
				);
			})}
		</Box>
	);
};

export default TransportModePicker;
